import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus } from 'lucide-react';
import { useTranslation } from '../../../i18n';

import { Card } from '../../../components/Shared/Card';
import { Loading } from '../../../components/Shared/Loading';
import { Button } from '../../../components/Shared/Button';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { Badge } from '../../../components/Shared/Badge';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../../components/Shared/Table';

import { useInvoiceStore } from '../../../store/useInvoiceStore';
import {
  billingIntervalLabelKey,
  formatBillingPeriod,
  isStandingInvoice,
} from '../../../types/billing';
import { formatMoney } from '@/utils/money';
import { BulkResultModal, type BulkResponse } from './BulkResultModal';
import { BillableRunFooter } from './BillableRunFooter';
import { NeedsReviewBadge } from './NeedsReviewBadge';
import {
  MONTH_KEYS,
  PERIOD_SELECT_CLASS,
  ROW_CHECKBOX_CLASS,
  isBlocked,
  YEAR_OPTIONS,
  useBillableSelection,
} from './billableRun';

interface MonthlyRunPanelProps {
  year: number;
  month: number;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
}

/**
 * Tab „Monatslauf“: proposal list for one month (FAFD-27). Monthly objects with everything due,
 * standing-invoice objects only with what comes on top — ad-hoc, one-off and by-effort work; their
 * fixed amounts are on the standing invoice. The „Abrechnung“ column makes that visible per row.
 */
export function MonthlyRunPanel({
  year, month, onYearChange, onMonthChange,
}: MonthlyRunPanelProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();

  const {
    billable, loadingBillable, fetchBillable,
    bulkCreate, saving,
  } = useInvoiceStore();

  const [bulkResult, setBulkResult] = useState<BulkResponse | null>(null);

  useEffect(() => {
    fetchBillable(year, month);
  }, [year, month, fetchBillable]);

  const selection = useBillableSelection(billable, `${year}-${month}`);

  const handleSubmit = async () => {
    if (selection.selectedIds.length === 0) return;
    try {
      const response = await bulkCreate({
        year, month,
        objectIds: selection.selectedIds,
        includeAdHoc: true,
      });
      if (response) {
        setBulkResult(response);
      }
    } catch {
      // showApiError already toasted; keep page state
    }
  };

  const closeBulkModal = () => {
    setBulkResult(null);
    // Refetch billable to reflect alreadyInvoiced changes
    fetchBillable(year, month);
    selection.clear();
  };

  const openFirstCreated = () => {
    const firstSuccess = bulkResult?.results?.find((r) => r.invoiceId);
    if (firstSuccess?.invoiceId) {
      navigate(`/invoices/${firstSuccess.invoiceId}`);
    } else {
      navigate('/invoices');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Period picker */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label htmlFor="monthly-run-year" className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
              {t('invoices:filter.year')}
            </label>
            <select
              id="monthly-run-year"
              value={year}
              onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
              className={PERIOD_SELECT_CLASS}
            >
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="monthly-run-month" className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">
              {t('invoices:filter.month')}
            </label>
            <select
              id="monthly-run-month"
              value={month}
              onChange={(e) => onMonthChange(parseInt(e.target.value, 10))}
              className={PERIOD_SELECT_CLASS}
            >
              {MONTH_KEYS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {t(`common:scheduleRule.months.${m}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Billable list */}
      {loadingBillable ? (
        <Loading />
      ) : billable.length === 0 ? (
        <EmptyState
          icon={<Receipt className="w-12 h-12" strokeWidth={1.75} />}
          title={t('invoices:billable.empty')}
          description={t('invoices:billable.emptyHint')}
          action={
            <Button
              variant="secondary"
              onClick={() => navigate('/invoices/new/manual')}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
            >
              {t('invoices:actions.createManual')}
            </Button>
          }
        />
      ) : (
        <>
          <Card noPadding>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-10">
                    <input
                      type="checkbox"
                      checked={selection.allSelectableSelected}
                      onChange={selection.toggleAll}
                      disabled={selection.selectable.length === 0}
                      aria-label={t('invoices:billable.selectAll')}
                      className={ROW_CHECKBOX_CLASS}
                    />
                  </TableHeader>
                  <TableHeader>{t('invoices:fields.customer')}</TableHeader>
                  <TableHeader>{t('invoices:fields.object')}</TableHeader>
                  <TableHeader className="hidden md:table-cell">
                    {t('invoices:billable.offerNumber')}
                  </TableHeader>
                  <TableHeader className="hidden md:table-cell">
                    {t('invoices:billable.interval')}
                  </TableHeader>
                  <TableHeader className="text-right">
                    {t('invoices:billable.recurringNet')}
                  </TableHeader>
                  <TableHeader className="hidden sm:table-cell text-right">
                    {t('invoices:billable.adHocCount')}
                  </TableHeader>
                  <TableHeader className="hidden sm:table-cell text-right">
                    {t('invoices:billable.adHocNet')}
                  </TableHeader>
                  <TableHeader className="text-right">
                    {t('invoices:billable.totalNet')}
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {billable.map((b) => {
                  const isInvoiced = b.alreadyInvoiced === true;
                  const isYearly = isStandingInvoice(b.billingInterval);
                  // why: a yearly object in the monthly run yields a MONTHLY ad-hoc invoice —
                  // the interval label stays the object's, the period is the month.
                  const periodInterval = isYearly ? 'monthly' : b.billingInterval;
                  return (
                    <TableRow key={b.objectId} className={isInvoiced ? 'opacity-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selection.isSelected(b.objectId)}
                          disabled={isInvoiced || isBlocked(b)}
                          onChange={() => selection.toggleOne(b.objectId)}
                          aria-label={b.objectName || ''}
                          className={ROW_CHECKBOX_CLASS}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-[13px] font-medium text-slate-900">
                          {b.customerName || '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] text-slate-700">{b.objectName || '—'}</span>
                          {isInvoiced && (
                            <Badge variant="warning">
                              {t('invoices:billable.alreadyInvoiced')}
                            </Badge>
                          )}
                          <NeedsReviewBadge row={b} />
                          {b.coveredByStandingInvoice && (
                            <Badge variant="info">
                              {t('invoices:billable.adHocOnlyHint')}
                            </Badge>
                          )}
                          {isYearly && !b.coveredByStandingInvoice && (b.standingInvoiceStarted ? (
                            // why: standing billing has begun, so the fixed amounts wait for the
                            // missing standing invoice — the monthly invoice has the extras only (FAFD-27).
                            <Badge variant="warning" title={t('invoices:billable.standingMissingHint')}>
                              {t('invoices:billable.standingMissing')}
                            </Badge>
                          ) : (
                            // why: before the first standing invoice the fixed amounts are billed
                            // monthly — none is lost (FAFD-27).
                            <Badge variant="warning" title={t('invoices:billable.notCoveredHint')}>
                              {t('invoices:billable.notCovered')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-[13px] text-slate-600 font-mono-num">
                        {b.offerNumber || '—'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="text-[13px] text-slate-700">
                          {t(billingIntervalLabelKey(b.billingInterval))}
                        </div>
                        <div className="text-[11px] text-slate-500 tabular-nums">
                          {formatBillingPeriod(year, b.periodStartMonth ?? month, periodInterval)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-[13px] text-slate-700 tabular-nums">
                        {formatMoney(b.recurringNet)}
                        {(b.oneOffNet ?? 0) > 0 && (
                          // why: one-off positions are on the created invoice too and part of
                          // the total — show them so the columns add up (FAFD-27).
                          <div className="text-[11px] text-slate-500">
                            {t('invoices:billable.oneOffNet', { amount: formatMoney(b.oneOffNet) })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-right text-[13px] text-slate-600 tabular-nums">
                        {b.adHocCount ?? 0}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-right text-[13px] text-slate-700 tabular-nums">
                        {formatMoney(b.adHocNet)}
                      </TableCell>
                      <TableCell className="text-right text-[13px] font-medium text-slate-900 tabular-nums">
                        {formatMoney(b.totalNet)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          <BillableRunFooter
            summary={t('invoices:billable.selectedSummary', {
              selected: selection.selectedIds.length,
              total: selection.selectable.length,
              amount: formatMoney(selection.selectedTotal),
            })}
            submitLabel={t('invoices:actions.bulkCreate')}
            disabled={selection.selectedIds.length === 0}
            saving={saving}
            onSubmit={handleSubmit}
          />
        </>
      )}

      {bulkResult && (
        <BulkResultModal
          response={bulkResult}
          rows={billable}
          onClose={closeBulkModal}
          onOpenCreated={openFirstCreated}
        />
      )}
    </div>
  );
}
